<template>
	<v-dialog v-model="visible" max-width="360">
		<template v-slot:activator="{ on, attrs }">
			<slot v-bind="{ on, attrs }" />
		</template>
		<v-card :loading="loading">
			<layout-error-boundary
				ref="errBoundary"
				v-slot="{ hasError, err }"
				:on-error="handleFormError"
			>
				<v-form ref="form" lazy-validation @submit.prevent="submit">
					<v-card-title class="headline">{{ strings.title }}</v-card-title>
					<v-card-text>
						<v-alert
							v-if="hasError"
							type="error"
							border="left"
							dismissible
							outlined
							@click.native="$refs.errBoundary.resetError()"
						>
							<span class="error--text">{{
								`${strings.error}${err.message ? ": " + err.message : ""}`
							}}</span>
						</v-alert>

						<p>{{ strings.body }}</p>
						<v-select v-model="destination" :items="listItems" label="Room" />
					</v-card-text>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn color="rust-red" text @click="close(false)">Cancel</v-btn>
						<v-btn color="ocean darken-1" outlined @click="close(true)"
							>Move</v-btn
						>
					</v-card-actions>
				</v-form>
			</layout-error-boundary>
		</v-card>
	</v-dialog>
</template>

<script>
import _ from "lodash";

import { NodeOperationError } from "@/utils/k4util";
import { APIError, APIAuthError } from "@/utils/http";
import strings from "@/utils/strings";

const initData = () => ({
	strings: strings.dialogs.move,
	visible: false,
	loading: false,

	resolve: null,
	handleSubmit: null,

	device: null,
	destination: null,
});

export default {
	props: {
		rooms: {
			type: Array,
			required: true,
		},
	},

	data: initData,

	computed: {
		listItems() {
			return _.map(this.rooms, room => ({
				text: room.name,
				value: room.meta ? room.meta.guid : room.guid,
			}));
		},
	},

	watch: {
		visible(is, was) {
			// Reset all relevant data whenever dialog closes
			if (was && !is) this.reset();
		},
	},

	methods: {
		reset() {
			Object.assign(this, initData());
			this.$refs.errBoundary.resetError();
			this.$refs.form.resetValidation();
		},

		pop(item, handleSubmit) {
			this.node = item;
			this.destination = null;
			this.handleSubmit = handleSubmit;
			this.visible = true;
			return new Promise(resolve => {
				this.resolve = resolve;
			});
		},

		async close(confirmed) {
			if (!confirmed) {
				this.visible = false;
				if (this.resolve) this.resolve(false);
				return;
			}

			const valid = this.$refs.form.validate();
			if (!valid) return;

			this.loading = true;

			const formPayload = {
				node: this.node,
				destination: this.destination,
			};
			try {
				let resp = formPayload;
				if (this.handleSubmit) {
					resp = await this.handleSubmit(formPayload);
				}
				if (this.resolve) {
					this.resolve(resp);
				}
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},

		handleFormError(err) {
			// We only want to handle certain types of errors. Keep propagating
			// any others.
			const isApiErr =
				err instanceof APIError && !(err instanceof APIAuthError);
			const isNodeOpErr = err instanceof NodeOperationError;
			return !(isApiErr || isNodeOpErr);
		},
	},
};
</script>
